import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Download, Lock, Unlock, Edit2, Tag, MessageSquare, Clock,
  ChevronDown, ChevronUp, Upload, Send, Loader2, FileText, Info, History
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const DocumentDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [tab, setTab] = useState('info');
  const [versionFile, setVersionFile] = useState(null);
  const [versionNotes, setVersionNotes] = useState('');
  const [uploadingVersion, setUploadingVersion] = useState(false);

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const fetchDoc = async () => {
    try {
      const res = await api.get(`/documents/documents/${id}`);
      setDoc(res.data.document);
    } catch (e) {
      toast.error('Document not found');
      navigate('/app/documents');
    }
    setLoading(false);
  };

  useEffect(() => { fetchDoc(); }, [id]);

  const handleDownload = async () => {
    try {
      const res = await api.get(`/documents/documents/${id}/download`);
      const fileUrl = res?.data?.fileUrl;

      if (!fileUrl) {
        throw new Error("No download URL");
      }

      window.open(fileUrl, "_blank");

    } catch (e) {
      console.error(e);
      toast.error('Download failed');
    }
  };

  const handleCheckout = async () => {
    try {
      if (doc.is_checked_out) {
        await api.post(`/documents/documents/${id}/checkin`);
        toast.success('Checked in successfully');
      } else {
        await api.post(`/documents/documents/${id}/checkout`);
        toast.success('Checked out successfully');
      }
      fetchDoc();
    } catch (e) { }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmittingComment(true);
    try {
      await api.post(`/documents/documents/${id}/comments`, { comment });
      setComment('');
      fetchDoc();
      toast.success('Comment added');
    } catch (e) { }
    setSubmittingComment(false);
  };


  // Handling the presigned url generation.
  const fetchPresignedUrl = async () => {
    try {
      const res = await api.post('/documents/presigned-url', { fileName: versionFile?.name, fileType: versionFile?.type, slug: JSON.parse(localStorage.getItem('dms_tenant')).slug });
      return res?.data;

    } catch (err) {
      console.error(err);
      toast.error("Something went wrong in uploading the file.");
    }
  }

  const handleUploadVersion = async (e) => {
    e.preventDefault();
    if (!versionFile) return;
    setUploadingVersion(true);

    try {

      //Uploading the file directly to S3 bucket from here.
      //Fetch the presigned uri.
      const { presignedUrl, s3_fileName, fileUrl } = await fetchPresignedUrl();
      console.log(presignedUrl, s3_fileName, fileUrl)

      console.log("Presigned URL: ", presignedUrl);

      //API call to presigned url to upload the file here.
      const response = await axios.put(presignedUrl, versionFile);
      console.log(response);

      if (response.status !== 200) {
        toast.error("Something went wrong in uploading the file.");
        return;
      }

      //Creating the database entry.
      await api.post(`/documents/documents/${id}/version`, {
        fileSize: versionFile.size,
        file_extension: versionFile.type,
        s3_fileName: s3_fileName,
        fileUrl: fileUrl,
        change_notes: versionNotes,
      });

      toast.success('New version uploaded!');
      setVersionFile(null);
      setVersionNotes('');
      fetchDoc();
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong in uploading the file.")
    } finally {
      setUploadingVersion(false);
    }
  };

  const formatSize = (bytes) => bytes < 1048576 ? (bytes / 1024).toFixed(1) + ' KB' : (bytes / 1048576).toFixed(1) + ' MB';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>;
  if (!doc) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Link to="/app/documents" className="btn-secondary !py-1.5 !px-3 mt-1"><ArrowLeft className="w-4 h-4" /></Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 leading-tight">{doc.title}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-gray-400">
            <span>v{doc.version_number}</span>
            <span>·</span>
            <span>{formatSize(doc.file_size)}</span>
            <span>·</span>
            <span>{doc.file_extension?.toUpperCase()}</span>
            <span>·</span>
            <span>by {doc.created_by_name}</span>
            <span>·</span>
            <span>{format(new Date(doc.updated_at), 'MMM d, yyyy')}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canEdit && (
            <button onClick={handleCheckout}
              className={`btn-secondary !py-1.5 !px-3 ${doc.is_checked_out ? 'text-orange-600 border-orange-300' : ''}`}>
              {doc.is_checked_out ? <><Unlock className="w-4 h-4" /> Check In</> : <><Lock className="w-4 h-4" /> Check Out</>}
            </button>
          )}
          <button onClick={handleDownload} className="btn-primary !py-1.5">
            <Download className="w-4 h-4" /> Download
          </button>
        </div>
      </div>

      {doc.is_checked_out && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5 text-sm text-orange-700 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Checked out by <strong>{doc.checked_out_by_name || 'a user'}</strong> · Editing locked for others
        </div>
      )}

      {/* Status badges */}
      <div className="flex gap-2 flex-wrap">
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${doc.status === 'published' ? 'bg-green-100 text-green-700' :
          doc.status === 'archived' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-700'
          }`}>{doc.status}</span>
        {doc.category_name && <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">{doc.category_name}</span>}
        {doc.tags?.map(tag => <span key={tag} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">#{tag}</span>)}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        {[
          { id: 'info', icon: Info, label: 'Info & Metadata' },
          { id: 'versions', icon: History, label: `Versions (${doc.versions?.length || 0})` },
          { id: 'comments', icon: MessageSquare, label: `Comments (${doc.comments?.length || 0})` },
        ].map(({ id: tabId, icon: Icon, label }) => (
          <button key={tabId} onClick={() => setTab(tabId)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === tabId ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card space-y-3">
            <h3 className="font-semibold text-gray-800">Document Info</h3>
            {doc.description && <p className="text-sm text-gray-600">{doc.description}</p>}
            <div className="space-y-2 text-sm">
              {[
                ['Original File', doc.original_filename],
                ['File Size', formatSize(doc.file_size)],
                ['File Type', doc.mime_type],
                ['Version', `v${doc.version_number}`],
                ['Status', doc.status],
                ['Category', doc.category_name || '—'],
                ['Created', format(new Date(doc.created_at), 'MMM d, yyyy HH:mm')],
                ['Updated', format(new Date(doc.updated_at), 'MMM d, yyyy HH:mm')],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between py-1 border-b border-gray-50">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-gray-900 font-medium text-right max-w-[60%] truncate">{val}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card space-y-3">
            <h3 className="font-semibold text-gray-800">Custom Metadata</h3>
            {doc.metadata?.length === 0 ? (
              <p className="text-gray-400 text-sm">No custom metadata fields</p>
            ) : (
              <div className="space-y-2">
                {doc.metadata?.map(m => (
                  <div key={m.meta_key} className="flex justify-between py-1 border-b border-gray-50 text-sm">
                    <span className="text-gray-500 font-mono text-xs">{m.meta_key}</span>
                    <span className="text-gray-900 font-medium">{m.meta_value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'versions' && (
        <div className="space-y-4">
          {canEdit && (
            <div className="card">
              <h3 className="font-semibold text-gray-800 mb-3">Upload New Version</h3>
              <form onSubmit={handleUploadVersion} className="space-y-3">
                <input type="file" className="input" onChange={e => setVersionFile(e.target.files[0])} />
                <input className="input" placeholder="Change notes (optional)" value={versionNotes}
                  onChange={e => setVersionNotes(e.target.value)} />
                <button type="submit" disabled={uploadingVersion || !versionFile} className="btn-primary disabled:opacity-60">
                  {uploadingVersion && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Upload className="w-4 h-4" /> Upload Version
                </button>
              </form>
            </div>
          )}
          <div className="card !p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {['Version', 'Size', 'Notes', 'Uploaded By', 'Date'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {doc.versions?.map(v => (
                  <tr key={v.id} className={v.version_number === doc.version_number ? 'bg-primary-50' : ''}>
                    <td className="px-4 py-3 font-mono text-xs">
                      v{v.version_number}
                      {v.version_number === doc.version_number && <span className="ml-2 text-xs bg-primary-600 text-white px-1.5 py-0.5 rounded">Current</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatSize(v.file_size)}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{v.change_notes || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{v.created_by_name}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{format(new Date(v.created_at), 'MMM d, yyyy')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'comments' && (
        <div className="space-y-4">
          <form onSubmit={handleComment} className="card flex gap-3">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold text-xs flex-shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 flex gap-2">
              <input className="input flex-1" placeholder="Add a comment..." value={comment}
                onChange={e => setComment(e.target.value)} />
              <button type="submit" disabled={submittingComment || !comment.trim()} className="btn-primary !py-2 !px-3 disabled:opacity-60">
                {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </form>
          <div className="space-y-3">
            {doc.comments?.length === 0 && <div className="text-center text-gray-400 py-8">No comments yet. Start the conversation!</div>}
            {doc.comments?.map(c => (
              <div key={c.id} className="card !p-4 flex gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-bold text-xs flex-shrink-0">
                  {c.user_name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{c.user_name}</span>
                    <span className="text-xs text-gray-400">{format(new Date(c.created_at), 'MMM d, yyyy HH:mm')}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{c.comment}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetail;
